import type {
  Brief,
  CourseCategory,
  ImageStyle,
  VideoMode,
} from '../../../types';
import {type MotionChoices, motionPreferences} from '../../../motionDesign';
import {languageLabel} from '../../../studioLanguages';
import {createText} from '../../../createText';
import {
  CreateForm,
  LIBRARY_MUSIC,
  LibraryFile,
  mediaSourceFor,
  NO_MUSIC,
  SourceType,
  StudioOptions,
  type VideoType,
} from '../createOptions';

/** What the brief is made from: the create form as the author left it. */
export interface BriefSource {
  form: CreateForm;
  options: StudioOptions;
  source: {
    type: SourceType;
    file: File | null;
    libraryFile: LibraryFile | null;
    hasFile: boolean;
  };
  narration: {voiceEmotion: string; showDelivery: boolean};
  musicFile: LibraryFile | null;
  videoMode: VideoMode;
  imageStyle: ImageStyle;
  /** The visuals sent: a video's or stock footage's "source content" becomes a mix. */
  visuals: string;
  /** Still images or stock footage, as the server can make them. */
  videoType: VideoType;
  canClip: boolean;
  clipping: boolean;
  perTopic: boolean;
  showImageStyle: boolean;
  /** The motion choices, while the server offers them. */
  motion: MotionChoices | null;
  /** The form shows the motion choices: the server offers them and the video is not clipped. */
  showMotion: boolean;
}

/** Why the form can't be sent yet, or null when it can. */
export function formProblem({
  form,
  source,
  clipping,
}: BriefSource): string | null {
  if (!form.title.trim()) return createText.titleRequired;
  if (source.type === 'prompt' && !form.prompt.trim())
    return createText.promptRequired;
  if (source.type === 'document' && !source.hasFile)
    return createText.sourceRequired;
  if (!clipping && !form.voiceName)
    return createText.chooseLibraryVoice(languageLabel(form.language));
  return null;
}

/**
 * The job's brief. `libraryMediaFileId` is the library file chosen as the source; a picked
 * video only has one once it is uploaded.
 */
export function createBrief(
  state: BriefSource,
  {
    category,
    excludedPages,
    libraryMediaFileId,
  }: {
    category: CourseCategory | null;
    excludedPages: number[];
    libraryMediaFileId: string | null;
  },
): Brief {
  const {form, source, narration, musicFile, perTopic} = state;
  const length = state.options.lengths.find(l => l.id === form.lengthBand);
  const {motionDesign} = state.options;
  return {
    ...form,
    subjectId: category?.id ?? null,
    // A clipped reel plays the video's own sound, with no music over it.
    backgroundMusic: state.clipping
      ? NO_MUSIC
      : form.backgroundMusic === LIBRARY_MUSIC && musicFile
      ? `library:${musicFile.id}`
      : form.backgroundMusic,
    // Only a server that can clip knows the setting; an older one animates.
    ...(state.canClip ? {videoMode: state.videoMode} : {}),
    ...(state.showImageStyle ? {imageStyle: state.imageStyle} : {}),
    // Only a server that offers motion design knows the setting; an older one keeps its visuals.
    ...(state.showMotion && motionDesign && state.motion
      ? {motion: motionPreferences(motionDesign, state.motion)}
      : {}),
    ...(narration.showDelivery && narration.voiceEmotion
      ? {voiceEmotion: narration.voiceEmotion}
      : {}),
    ending: form.ending as Brief['ending'],
    visuals: state.visuals as Brief['visuals'],
    // Stock footage only where the server has it, whatever the form kept from before.
    mediaSource: mediaSourceFor(state.videoType),
    sourceType: source.type,
    libraryMediaFileId,
    excludedPages,
    reelMode: perTopic ? 'per_topic' : 'single',
    // No cap: every topic found becomes a microreel unless the author unticks it.
    maxTopics: null,
    documentName:
      source.type === 'document'
        ? source.libraryFile?.fileName || source.file?.name
        : null,
    title: form.title.trim(),
    // Each topic becomes its own microreel, so a focus typed earlier is not sent.
    prompt: perTopic ? null : form.prompt.trim() || null,
    targetSeconds: length?.targetSeconds ?? 50,
  };
}
